import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dog, Ticket, CheckCircle2, Percent, Users } from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ChartBox } from "@/components/ChartBox";

const COLORS = ["hsl(28 33% 48%)", "hsl(24 40% 34%)", "hsl(38 45% 56%)", "hsl(30 18% 60%)", "hsl(200 14% 46%)"];
const cap = (s) => String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function MiniStat({ label, value, testid }) {
  return (
    <Card className="border-border" data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-2xl font-heading font-semibold">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports").then((r) => setData(r.data)).catch(() => {}); }, []);

  const kpis = data ? [
    { label: "Active Dogs", value: data.active_dogs, icon: Dog },
    { label: "Active Clients", value: data.active_clients, icon: Users },
    { label: "Total Bookings", value: data.total_bookings, icon: Ticket },
    { label: "Completed Classes", value: data.completed_classes, icon: CheckCircle2 },
    { label: "Attendance Rate", value: `${data.attendance_rate}%`, icon: Percent },
  ] : [];

  const certData = (data?.trainers_by_certification || []).map((d) => ({ name: cap(d.name), value: d.value }));
  const levelData = (data?.dogs_by_level || []).map((d) => ({ name: cap(d.name), value: d.value }));

  return (
    <div>
      <PageHeader title="Reports" subtitle="Key operational metrics across your network." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpis.map((k) => (
          <Card key={k.label} className="border-border" data-testid={`report-kpi-${k.label.toLowerCase().replace(/ /g, "-")}`}>
            <CardContent className="p-5">
              <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center mb-3"><k.icon size={18} className="text-primary" strokeWidth={1.5} /></div>
              <div className="text-3xl font-heading font-semibold">{k.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 md:grid-cols-3 gap-4 mb-6">
          <MiniStat label="Confirmed Bookings" value={data.bookings_confirmed} testid="report-confirmed" />
          <MiniStat label="Waitlisted" value={data.bookings_waitlisted} testid="report-waitlisted" />
          <MiniStat label="Requested" value={data.bookings_requested} testid="report-requested" />
          <MiniStat label="No-shows" value={data.no_show_count} testid="report-no-show" />
          <MiniStat label="Capacity Usage" value={`${data.capacity_usage}%`} testid="report-capacity" />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-lg">Dogs by Training Level</CardTitle></CardHeader>
          <CardContent>
            <ChartBox height={288}>
              <BarChart data={levelData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(34 22% 87%)" vertical={false} />
                <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "hsl(34 24% 91%)" }} />
                <Bar dataKey="value" fill="hsl(28 33% 48%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ChartBox>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-lg">Trainers by Certification</CardTitle></CardHeader>
          <CardContent>
            <ChartBox height={288}>
              <PieChart>
                <Pie data={certData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {certData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ChartBox>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border overflow-hidden">
        <CardHeader><CardTitle className="font-heading text-lg">Franchise Location Activity</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table data-testid="reports-location-table">
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                {["Location", "Dogs", "Classes", "Trainers", "Status"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wide font-semibold">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.location_activity || []).map((l) => (
                <TableRow key={l.name} className="hover:bg-muted/40">
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell>{l.dogs}</TableCell>
                  <TableCell>{l.classes}</TableCell>
                  <TableCell>{l.trainers}</TableCell>
                  <TableCell><StatusBadge value={l.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
